/**
 * RichFormatSupport.ts - Rich format clipboard support
 * 
 * CHANGE HISTORY:
 * - 251102_1225 [pending] danielbeer: Created rich format support for RTF and Office formats
 * 
 * Handles rich text formats (RTF) and Office document formats.
 */

import type { ClipboardFormat, FormatDetectionResult } from '../types';

/**
 * Rich format support for RTF and Office documents
 */
export class RichFormatSupport {
  
  /**
   * Detect RTF format
   */
  static isRTF(data: string): boolean {
    if (typeof data !== 'string') return false;
    return data.trim().startsWith('{\\rtf');
  }
  
  /**
   * Detect Office formats
   */
  static isOfficeFormat(mimeType: string): boolean {
    const officeFormats = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',      // .xlsx
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/msword',                                                       // .doc
      'application/vnd.ms-excel',                                                // .xls
      'application/vnd.ms-powerpoint',                                           // .ppt
      'application/vnd.oasis.opendocument.text',                                 // .odt
      'application/vnd.oasis.opendocument.spreadsheet',                          // .ods
      'application/vnd.oasis.opendocument.presentation'                          // .odp
    ];
    
    return officeFormats.includes(mimeType);
  }
  
  /**
   * Detect rich format
   */
  static detect(data: any): FormatDetectionResult | null {
    // Check for RTF
    if (typeof data === 'string' && this.isRTF(data)) {
      return {
        format: 'text/rtf',
        confidence: 1.0
      };
    }
    
    // Check for Office formats (would need File/Blob)
    if (data instanceof Blob || data instanceof File) {
      if (this.isOfficeFormat(data.type)) {
        return {
          format: data.type as ClipboardFormat,
          confidence: 1.0
        };
      }
    }
    
    return null;
  }
  
  /**
   * Convert RTF to plain text
   * Basic conversion - strips RTF control words
   */
  static rtfToText(rtf: string): string {
    if (!this.isRTF(rtf)) {
      return rtf;
    }
    
    let text = rtf;
    
    // Remove RTF header
    text = text.replace(/^\{\\rtf[^}]*\}/, '');
    
    // Remove control words
    text = text.replace(/\\[a-z]+(-?\d+)?[ ]?/g, '');
    
    // Remove control symbols
    text = text.replace(/[{}]/g, '');
    
    // Decode special characters
    text = text.replace(/\\'([0-9a-fA-F]{2})/g, (_match, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });
    
    // Clean up whitespace
    text = text.replace(/\s+/g, ' ').trim();
    
    return text;
  }
  
  /**
   * Convert RTF to HTML
   * Basic conversion - preserves basic formatting
   */
  static rtfToHTML(rtf: string): string {
    if (!this.isRTF(rtf)) {
      return rtf;
    }
    
    let html = rtf;
    
    // Remove RTF header
    html = html.replace(/^\{\\rtf[^}]*\}/, '');
    
    // Convert bold
    html = html.replace(/\\b\s+(.*?)\\b0/g, '<strong>$1</strong>');
    
    // Convert italic
    html = html.replace(/\\i\s+(.*?)\\i0/g, '<em>$1</em>');
    
    // Convert underline
    html = html.replace(/\\ul\s+(.*?)\\ulnone/g, '<u>$1</u>');
    
    // Convert paragraphs
    html = html.replace(/\\par\s*/g, '</p><p>');
    
    // Remove remaining control words
    html = html.replace(/\\[a-z]+(-?\d+)?[ ]?/g, '');
    
    // Remove braces
    html = html.replace(/[{}]/g, '');
    
    // Wrap in paragraph
    html = `<p>${html}</p>`;
    
    // Clean up empty paragraphs
    html = html.replace(/<p>\s*<\/p>/g, '');
    
    return html;
  }
  
  /**
   * Convert plain text to RTF
   */
  static textToRTF(text: string): string {
    // Basic RTF header
    let rtf = '{\\rtf1\\ansi\\deff0 ';
    
    // Escape special characters
    text = text.replace(/\\/g, '\\\\');
    text = text.replace(/{/g, '\\{');
    text = text.replace(/}/g, '\\}');
    
    // Convert newlines to RTF paragraphs
    text = text.replace(/\n/g, '\\par ');
    
    rtf += text;
    rtf += '}';
    
    return rtf;
  }
  
  /**
   * Convert HTML to RTF
   * Basic conversion - preserves basic formatting
   */
  static htmlToRTF(html: string): string {
    let rtf = '{\\rtf1\\ansi\\deff0 ';
    
    // Remove HTML tags but preserve formatting
    let text = html;
    
    // Convert strong/b to RTF bold
    text = text.replace(/<(strong|b)>(.*?)<\/(strong|b)>/gi, '\\b $2\\b0 ');
    
    // Convert em/i to RTF italic
    text = text.replace(/<(em|i)>(.*?)<\/(em|i)>/gi, '\\i $2\\i0 ');
    
    // Convert u to RTF underline
    text = text.replace(/<u>(.*?)<\/u>/gi, '\\ul $1\\ulnone ');
    
    // Convert paragraphs
    text = text.replace(/<p>/gi, '');
    text = text.replace(/<\/p>/gi, '\\par ');
    
    // Convert line breaks
    text = text.replace(/<br\s*\/?>/gi, '\\par ');
    
    // Remove remaining HTML tags
    text = text.replace(/<[^>]+>/g, '');
    
    // Escape special characters
    text = text.replace(/\\/g, '\\\\');
    text = text.replace(/{/g, '\\{');
    text = text.replace(/}/g, '\\}');
    
    rtf += text;
    rtf += '}';
    
    return rtf;
  }
  
  /**
   * Get Office format metadata
   */
  static getOfficeMetadata(file: File): {
    type: string;
    extension: string;
    category: 'word' | 'excel' | 'powerpoint' | 'other';
  } {
    const mimeType = file.type;
    const name = file.name;
    const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
    
    let category: 'word' | 'excel' | 'powerpoint' | 'other' = 'other';
    
    if (mimeType.includes('word') || ext === 'doc' || ext === 'docx' || ext === 'odt') {
      category = 'word';
    } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || ext === 'xls' || ext === 'xlsx' || ext === 'ods') {
      category = 'excel';
    } else if (mimeType.includes('powerpoint') || mimeType.includes('presentation') || ext === 'ppt' || ext === 'pptx' || ext === 'odp') {
      category = 'powerpoint';
    }
    
    return {
      type: mimeType,
      extension: ext,
      category
    };
  }
  
  /**
   * Check if format is rich text
   */
  static isRichFormat(format: ClipboardFormat): boolean {
    return (
      format === 'text/rtf' ||
      this.isOfficeFormat(format)
    );
  }
  
  /**
   * Get format label
   */
  static getFormatLabel(format: ClipboardFormat): string {
    const labels: Record<string, string> = {
      'text/rtf': 'Rich Text Format',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document (.docx)',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet (.xlsx)',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint Presentation (.pptx)',
      'application/msword': 'Word Document (.doc)',
      'application/vnd.ms-excel': 'Excel Spreadsheet (.xls)',
      'application/vnd.ms-powerpoint': 'PowerPoint Presentation (.ppt)',
      'application/vnd.oasis.opendocument.text': 'OpenDocument Text (.odt)',
      'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument Spreadsheet (.ods)',
      'application/vnd.oasis.opendocument.presentation': 'OpenDocument Presentation (.odp)'
    };
    
    return labels[format] || format;
  }
}
